import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Film } from "lucide-react";
import { CATEGORIES, CATEGORY_HINTS } from "./constants";
import { logSwallow } from "../../lib/log";
import GifPicker from "../gifs/GifPicker";

/**
 * VibeComposer — text input, category chips, submit.
 *
 * First-tap category hints: the very first time a user taps each category we
 * flash a small explanatory tooltip above the chip row (auto-dismiss ~4.5s).
 * Persistence via localStorage `bl_cat_seen_<ID>` — subsequent taps just
 * select silently. Matches the "contextual hint" language used on the Home
 * screen (dark glass background, white check chip, red glow).
 *
 * iOS Safari keyboard-zoom is prevented by pinning input font-size to 16px.
 */
export default function VibeComposer({
  text, setText,
  category, setCategory,
  busy, onSubmit, onGifSelect,
}) {
  const [hint, setHint] = useState({ show: false, text: "" });
  const [gifOpen, setGifOpen] = useState(false);
  const hintTimerRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => () => {
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
  }, []);

  const hasSeenHint = (id) => {
    try { return !!localStorage.getItem(`bl_cat_seen_${id}`); }
    catch (e) { logSwallow("composer.cat.seen.read", e); return false; }
  };
  const markSeen = (id) => {
    try { localStorage.setItem(`bl_cat_seen_${id}`, "1"); }
    catch (e) { logSwallow("composer.cat.seen.write", e); }
  };

  const onPickCategory = (id) => {
    // Always select — hint never blocks the tap (spec: "first tap should
    // select the category and display its contextual explanation").
    setCategory(id);
    if (!hasSeenHint(id)) {
      const msg = CATEGORY_HINTS[id];
      if (msg) {
        if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
        setHint({ show: true, text: msg });
        hintTimerRef.current = setTimeout(
          () => setHint((h) => ({ ...h, show: false })),
          4500,
        );
        markSeen(id);
      }
    }
  };

  return (
    <form onSubmit={onSubmit}
      className="rounded-3xl border border-[#E60000]/40 bg-black/60 shadow-[0_0_40px_rgba(230,0,0,0.15)] p-4"
      data-testid="vibe-composer"
    >
      <div className="flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 120))}
          placeholder={t("liveRoom.placeholder")}
          className="flex-1 min-w-0 bg-black/60 border border-white/10 rounded-full px-5 py-3 outline-none focus:border-[#E60000]"
          style={{ fontSize: "16px" }}
          autoComplete="off"
          autoCapitalize="sentences"
          autoCorrect="on"
          data-testid="vibe-input"
        />
        <button
          type="button"
          onClick={() => setGifOpen(true)}
          data-testid="vibe-gif-btn"
          title="Add a GIF"
          className="shrink-0 h-12 px-4 rounded-full border border-white/10 text-zinc-300 hover:border-[#E60000]/50 hover:text-white transition flex items-center gap-1.5"
        >
          <Film size={17} /> <span className="text-[12px] font-mono-accent">{t("liveRoom.gif")}</span>
        </button>
      </div>

      <p className="mt-3 text-zinc-500 text-xs italic">{t("liveRoom.tryExample")}</p>
      <p className="text-zinc-600 text-xs mt-1">{t("liveRoom.appearsLive")}</p>

      {/* Chip row with the first-tap hint anchored above it */}
      <div className="mt-3 relative">
        <div
          className={`absolute left-1/2 bottom-full mb-2 -translate-x-1/2 z-30 pointer-events-none transition-all duration-300 ease-out ${
            hint.show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
          }`}
          style={{ width: "min(320px, calc(100vw - 2rem))" }}
          data-testid="category-hint"
          data-visible={hint.show ? "true" : "false"}
          aria-live="polite"
        >
          <div className="rounded-2xl border border-white/10 bg-black/85 backdrop-blur-xl shadow-[0_0_24px_rgba(230,0,0,0.2)] px-3.5 py-2.5 flex items-start gap-3">
            <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center">
              <Check size={12} className="text-black" strokeWidth={3} />
            </span>
            <p className="text-white text-[0.82rem] leading-snug" data-testid="category-hint-text">
              {hint.text || ""}
            </p>
          </div>
          <div className="mx-auto -mt-1 w-2.5 h-2.5 rotate-45 bg-black/85 border-r border-b border-white/10" />
        </div>

        <div className="flex gap-2 flex-wrap" data-testid="vibe-categories">
          {CATEGORIES.map(c => (
            <button
              type="button"
              key={c.id}
              onClick={() => onPickCategory(c.id)}
              data-testid={`vibe-cat-${c.id}`}
              className={`px-3 py-1.5 rounded-full text-[10px] font-mono-accent transition ${
                category === c.id ? "text-white shadow-[0_0_12px_rgba(230,0,0,0.4)]" : "text-zinc-500 hover:text-white"
              }`}
              style={category === c.id
                ? { background: c.color, borderColor: c.color }
                : { border: `1px solid ${c.color}30` }}
            >
              {t(`categories.${c.id}`, c.label).toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <button
        disabled={busy || !text.trim()}
        data-testid="drop-vibe-btn"
        className="mt-4 w-full h-12 rounded-full bg-gradient-to-r from-[#E60000] to-[#660000] hover:brightness-110 disabled:opacity-40 transition font-bold text-base tracking-wide shadow-[0_0_24px_rgba(230,0,0,0.45)]"
      >
        {t("liveRoom.post")}
      </button>

      <GifPicker
        open={gifOpen}
        onClose={() => setGifOpen(false)}
        onSelect={(g) => onGifSelect?.(g)}
      />
    </form>
  );
}
